import { useState, useRef, useCallback } from "react";
import {
  Upload, ChevronRight, ChevronLeft, Check, X, Plus, Trash2,
  AlertTriangle, Loader2, FileImage, RotateCcw, PackageOpen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ExtractedCategory {
  name: string;
  sort_order: number;
}

interface ExtractedItem {
  name: string;
  description: string | null;
  category: string;
  price: number | null;
  takeout_price: number | null;
  delivery_price: number | null;
}

interface ExtractedAnswer {
  text: string;
  price: number;
  sort_order: number;
}

interface ExtractedModifierGroup {
  name: string;
  format: "radio" | "checkbox";
  required: boolean;
  min_select: number;
  max_select: number;
  answers: ExtractedAnswer[];
  suggested_items: string[];
  linkedItems?: Set<string>;
}

interface MenuImportWizardProps {
  open: boolean;
  onClose: () => void;
  restaurantId: string;
  menuId: string;
  token: string;
}

// ─── Step indicator ───────────────────────────────────────────────────────────

const STEPS = ["Upload", "Categories & Items", "Modifiers", "Confirm", "Done"];

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center gap-1">
      {STEPS.map((label, idx) => {
        const done = idx < current;
        const active = idx === current;
        return (
          <div key={idx} className="flex items-center gap-1">
            <div className={`flex items-center justify-center rounded-full w-6 h-6 text-xs font-semibold shrink-0
              ${done ? "bg-primary text-primary-foreground" : active ? "bg-primary/20 text-primary border border-primary/50" : "bg-muted text-muted-foreground"}`}
            >
              {done ? <Check className="w-3 h-3" /> : idx + 1}
            </div>
            <span className={`text-xs hidden sm:inline ${active ? "font-medium" : "text-muted-foreground"}`}>{label}</span>
            {idx < STEPS.length - 1 && <div className="w-4 h-px bg-border mx-1" />}
          </div>
        );
      })}
    </div>
  );
}

// ─── Step 1: Upload ───────────────────────────────────────────────────────────

function UploadStep({
  token,
  onExtracted,
}: {
  token: string;
  onExtracted: (data: { categories: ExtractedCategory[]; items: ExtractedItem[]; modifier_groups: ExtractedModifierGroup[] }) => void;
}) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rawAiError, setRawAiError] = useState<string | null>(null);

  const MAX_MB = 20;

  function handleFile(f: File) {
    setError(null);
    setRawAiError(null);
    if (f.size > MAX_MB * 1024 * 1024) {
      setError(`File too large. Maximum size is ${MAX_MB} MB.`);
      return;
    }
    const allowed = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    if (!allowed.includes(f.type)) {
      setError("Only JPG, PNG, WebP images and PDF files are supported.");
      return;
    }
    setFile(f);
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }

  async function handleExtract() {
    if (!file) return;
    setLoading(true);
    setError(null);
    setRawAiError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const jwt = localStorage.getItem("auth_token") || localStorage.getItem("token");
      const res = await fetch("/api/admin/menu-import/extract", {
        method: "POST",
        headers: {
          "x-eatzy-token": token,
          ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.raw) setRawAiError(data.raw);
        setError(data.error || "Extraction failed");
        return;
      }
      onExtracted(data);
    } catch (e: any) {
      setError(e.message || "Network error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-6 py-2">
      <div>
        <h3 className="font-semibold text-base mb-1">Upload your menu</h3>
        <p className="text-sm text-muted-foreground">
          Supported formats: JPG, PNG, WebP, PDF — up to {MAX_MB} MB. For best results, use a high-resolution image or a multi-page PDF.
        </p>
      </div>

      <div
        className="border-2 border-dashed rounded-md p-8 text-center cursor-pointer hover-elevate transition-colors"
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
        />
        {file ? (
          <div className="flex flex-col items-center gap-2">
            <FileImage className="w-8 h-8 text-primary" />
            <p className="font-medium text-sm">{file.name}</p>
            <p className="text-xs text-muted-foreground">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setFile(null); }}>
              <X className="w-3 h-3 mr-1" /> Remove
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className="w-8 h-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Drag and drop or click to select</p>
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 flex gap-2 text-sm text-destructive">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <p>{error}</p>
            {rawAiError && (
              <details className="mt-2">
                <summary className="cursor-pointer text-xs underline">Show AI response</summary>
                <pre className="text-xs mt-1 whitespace-pre-wrap break-all text-muted-foreground">{rawAiError}</pre>
              </details>
            )}
          </div>
        </div>
      )}

      <Button disabled={!file || loading} onClick={handleExtract} className="w-full">
        {loading ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reading your menu…</>
        ) : (
          <><ChevronRight className="w-4 h-4 mr-2" /> Extract Menu with AI</>
        )}
      </Button>
    </div>
  );
}

// ─── Step 2: Review Categories & Items ────────────────────────────────────────

type StagedItem = ExtractedItem & { _sid: string };

let _sidCounter = 0;
function makeSid() { return `sid-${++_sidCounter}`; }
function toStaged(items: ExtractedItem[]): StagedItem[] {
  return items.map(it => ({ ...it, _sid: makeSid() }));
}

function CategoriesItemsStep({
  categories,
  items,
  onUpdate,
  onBack,
  onNext,
}: {
  categories: ExtractedCategory[];
  items: ExtractedItem[];
  onUpdate: (cats: ExtractedCategory[], itms: ExtractedItem[]) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const [cats, setCats] = useState<ExtractedCategory[]>(categories);
  const [itms, setItms] = useState<StagedItem[]>(() => toStaged(items));
  const [catFilter, setCatFilter] = useState<string>("__all__");
  const [expandedSids, setExpandedSids] = useState<Set<string>>(new Set());
  const [selectedSids, setSelectedSids] = useState<Set<string>>(new Set());

  function updateCat(idx: number, field: keyof ExtractedCategory, value: any) {
    const next = [...cats];
    next[idx] = { ...next[idx], [field]: value };
    setCats(next);
  }

  function addCat() {
    setCats([...cats, { name: "", sort_order: cats.length }]);
  }

  function deleteCat(idx: number) {
    const removed = cats[idx].name;
    setCats(cats.filter((_, i) => i !== idx));
    setItms(itms.map(it => it.category === removed ? { ...it, category: "" } : it));
  }

  function updateItem(sid: string, field: keyof ExtractedItem, value: any) {
    setItms(prev => prev.map(it => it._sid === sid ? { ...it, [field]: value } : it));
  }

  function deleteItem(sid: string) {
    setItms(prev => prev.filter(it => it._sid !== sid));
    setSelectedSids(prev => { const next = new Set(prev); next.delete(sid); return next; });
  }

  function deleteSelected() {
    setItms(prev => prev.filter(it => !selectedSids.has(it._sid)));
    setSelectedSids(new Set());
  }

  function toggleSelect(sid: string) {
    setSelectedSids(prev => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid); else next.add(sid);
      return next;
    });
  }

  function toggleSelectAll() {
    const visibleSids = filteredItems.map(it => it._sid);
    const allSelected = visibleSids.every(s => selectedSids.has(s));
    setSelectedSids(prev => {
      const next = new Set(prev);
      if (allSelected) { visibleSids.forEach(s => next.delete(s)); }
      else { visibleSids.forEach(s => next.add(s)); }
      return next;
    });
  }

  function addItem() {
    setItms(prev => [...prev, { _sid: makeSid(), name: "", description: null, category: cats[0]?.name ?? "", price: null, takeout_price: null, delivery_price: null }]);
  }

  function handleBack() {
    onUpdate(cats, itms.map(({ _sid: _, ...rest }) => rest as ExtractedItem));
    onBack();
  }

  function handleNext() {
    onUpdate(cats, itms.map(({ _sid: _, ...rest }) => rest as ExtractedItem));
    onNext();
  }

  const filteredItems: StagedItem[] = catFilter === "__all__" ? itms : itms.filter(it => it.category === catFilter);
  const validCats = cats.filter(c => c.name.trim());

  return (
    <div className="space-y-4 py-2">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 min-h-0">
        {/* Categories */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Categories</span>
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={addCat}>
              <Plus className="w-3 h-3" />
            </Button>
          </div>
          <div className="space-y-1.5 max-h-[400px] overflow-y-auto pr-1">
            {cats.map((cat, idx) => (
              <div key={idx} className="flex items-center gap-1">
                <Input
                  value={cat.name}
                  onChange={(e) => updateCat(idx, "name", e.target.value)}
                  placeholder="Category name"
                  className="h-8 text-sm flex-1"
                />
                <Input
                  type="number"
                  value={cat.sort_order}
                  onChange={(e) => updateCat(idx, "sort_order", Number(e.target.value))}
                  className="h-8 text-sm w-14"
                  min={0}
                />
                <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => deleteCat(idx)}>
                  <Trash2 className="w-3 h-3 text-muted-foreground" />
                </Button>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">{cats.length} categories</p>
        </div>

        {/* Items */}
        <div className="md:col-span-2 space-y-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {itms.length} items across {validCats.length} categories
              {selectedSids.size > 0 && <span className="ml-2 text-primary">({selectedSids.size} selected)</span>}
            </span>
            <div className="flex items-center gap-2">
              {selectedSids.size > 0 && (
                <Button size="sm" variant="outline" onClick={deleteSelected}>
                  <Trash2 className="w-3 h-3 mr-1" /> Delete selected
                </Button>
              )}
              <Select value={catFilter} onValueChange={setCatFilter}>
                <SelectTrigger className="h-7 text-xs w-36">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All categories</SelectItem>
                  {validCats.map((c, i) => (
                    <SelectItem key={i} value={c.name}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={addItem}>
                <Plus className="w-3 h-3 mr-1" /> Add item
              </Button>
            </div>
          </div>

          <div className="border rounded-md overflow-auto max-h-[400px]">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted/80 backdrop-blur-sm">
                <tr>
                  <th className="p-2 w-8">
                    <input
                      type="checkbox"
                      className="cursor-pointer"
                      checked={filteredItems.length > 0 && filteredItems.every(it => selectedSids.has(it._sid))}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th className="text-left p-2 font-medium">Category</th>
                  <th className="text-left p-2 font-medium">Name</th>
                  <th className="text-left p-2 font-medium">Description</th>
                  <th className="text-right p-2 font-medium">Base $</th>
                  <th className="text-right p-2 font-medium">TOut $</th>
                  <th className="text-right p-2 font-medium">Del $</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                    <tr key={item._sid} className={`border-t hover:bg-muted/30 ${selectedSids.has(item._sid) ? "bg-muted/20" : ""}`}>
                      <td className="p-1 w-8 text-center">
                        <input
                          type="checkbox"
                          className="cursor-pointer"
                          checked={selectedSids.has(item._sid)}
                          onChange={() => toggleSelect(item._sid)}
                        />
                      </td>
                      <td className="p-1 min-w-[90px]">
                        <Select
                          value={item.category || "__none__"}
                          onValueChange={(v) => updateItem(item._sid, "category", v === "__none__" ? "" : v)}
                        >
                          <SelectTrigger className="h-7 text-xs">
                            <SelectValue placeholder="Category" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">—</SelectItem>
                            {validCats.map((c, ci) => (
                              <SelectItem key={ci} value={c.name}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-1 min-w-[120px]">
                        <Input
                          value={item.name}
                          onChange={(e) => updateItem(item._sid, "name", e.target.value)}
                          className="h-7 text-xs"
                        />
                      </td>
                      <td className="p-1 min-w-[140px]">
                        {expandedSids.has(item._sid) ? (
                          <Textarea
                            value={item.description ?? ""}
                            onChange={(e) => updateItem(item._sid, "description", e.target.value)}
                            className="text-xs min-h-[56px] resize-none"
                            onBlur={() => setExpandedSids(s => { const ns = new Set(s); ns.delete(item._sid); return ns; })}
                            autoFocus
                          />
                        ) : (
                          <div
                            className="text-xs text-muted-foreground cursor-pointer truncate max-w-[140px] hover:text-foreground"
                            title={item.description ?? ""}
                            onClick={() => setExpandedSids(s => new Set(s).add(item._sid))}
                          >
                            {item.description || <span className="italic">—</span>}
                          </div>
                        )}
                      </td>
                      <td className="p-1 min-w-[70px]">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.price ?? ""}
                          onChange={(e) => updateItem(item._sid, "price", e.target.value === "" ? null : Number(e.target.value))}
                          className="h-7 text-xs text-right"
                        />
                      </td>
                      <td className="p-1 min-w-[70px]">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.takeout_price ?? ""}
                          onChange={(e) => updateItem(item._sid, "takeout_price", e.target.value === "" ? null : Number(e.target.value))}
                          className="h-7 text-xs text-right"
                        />
                      </td>
                      <td className="p-1 min-w-[70px]">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.delivery_price ?? ""}
                          onChange={(e) => updateItem(item._sid, "delivery_price", e.target.value === "" ? null : Number(e.target.value))}
                          className="h-7 text-xs text-right"
                        />
                      </td>
                      <td className="p-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => deleteItem(item._sid)}>
                          <Trash2 className="w-3 h-3 text-muted-foreground" />
                        </Button>
                      </td>
                    </tr>
                ))}
                {filteredItems.length === 0 && (
                  <tr>
                    <td colSpan={8} className="p-4 text-center text-muted-foreground">No items</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="flex gap-2 justify-between pt-2">
        <Button variant="outline" onClick={handleBack}>
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button onClick={handleNext}>
          Next <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

// ─── Step 3: Modifier Groups ──────────────────────────────────────────────────

function ModifierStep({
  modifierGroups,
  items,
  onUpdate,
  onBack,
  onNext,
  onSkip,
}: {
  modifierGroups: ExtractedModifierGroup[];
  items: ExtractedItem[];
  onUpdate: (groups: ExtractedModifierGroup[]) => void;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
}) {
  const [groups, setGroups] = useState<ExtractedModifierGroup[]>(() =>
    modifierGroups.map(g => ({
      ...g,
      linkedItems: new Set(
        items
          .filter(it => g.suggested_items.some(si => it.name.toLowerCase().includes(si.toLowerCase()) || si.toLowerCase().includes(it.name.toLowerCase())))
          .map(it => it.name)
      ),
    }))
  );

  function updateGroup(idx: number, field: keyof ExtractedModifierGroup, value: any) {
    const next = [...groups];
    next[idx] = { ...next[idx], [field]: value };
    setGroups(next);
  }

  function updateAnswer(gIdx: number, aIdx: number, field: keyof ExtractedAnswer, value: any) {
    const next = [...groups];
    const answers = [...next[gIdx].answers];
    answers[aIdx] = { ...answers[aIdx], [field]: value };
    next[gIdx] = { ...next[gIdx], answers };
    setGroups(next);
  }

  function addAnswer(gIdx: number) {
    const next = [...groups];
    next[gIdx] = { ...next[gIdx], answers: [...next[gIdx].answers, { text: "", price: 0, sort_order: next[gIdx].answers.length }] };
    setGroups(next);
  }

  function removeAnswer(gIdx: number, aIdx: number) {
    const next = [...groups];
    next[gIdx] = { ...next[gIdx], answers: next[gIdx].answers.filter((_, i) => i !== aIdx) };
    setGroups(next);
  }

  function removeGroup(idx: number) {
    setGroups(groups.filter((_, i) => i !== idx));
  }

  function addGroup() {
    setGroups([...groups, {
      name: "",
      format: "radio",
      required: false,
      min_select: 0,
      max_select: 1,
      answers: [],
      suggested_items: [],
      linkedItems: new Set(),
    }]);
  }

  function toggleItemLink(gIdx: number, itemName: string) {
    const next = [...groups];
    const linked = new Set(next[gIdx].linkedItems ?? []);
    if (linked.has(itemName)) linked.delete(itemName);
    else linked.add(itemName);
    next[gIdx] = { ...next[gIdx], linkedItems: linked };
    setGroups(next);
  }

  function handleBack() {
    onUpdate(groups);
    onBack();
  }

  function handleNext() {
    onUpdate(groups);
    onNext();
  }

  return (
    <div className="space-y-4 py-2">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-sm">Modifier Groups</h3>
          <p className="text-xs text-muted-foreground">Review modifier groups extracted from your menu. You can skip this step.</p>
        </div>
        <Button variant="outline" size="sm" onClick={addGroup}>
          <Plus className="w-3 h-3 mr-1" /> Add Group
        </Button>
      </div>

      <div className="space-y-4 max-h-[420px] overflow-y-auto pr-1">
        {groups.length === 0 && (
          <div className="text-center py-8 text-sm text-muted-foreground">
            No modifier groups. You can add them or skip this step.
          </div>
        )}
        {groups.map((group, gIdx) => (
          <div key={gIdx} className="border rounded-md p-3 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Group Name</Label>
                    <Input
                      value={group.name}
                      onChange={(e) => updateGroup(gIdx, "name", e.target.value)}
                      className="h-8 text-sm mt-1"
                      placeholder="e.g. Size, Toppings"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Format</Label>
                    <Select value={group.format} onValueChange={(v) => updateGroup(gIdx, "format", v)}>
                      <SelectTrigger className="h-8 text-sm mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="radio">Radio (choose one)</SelectItem>
                        <SelectItem value="checkbox">Checkbox (choose any)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 items-center">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={group.required}
                      onCheckedChange={(v) => updateGroup(gIdx, "required", v)}
                      className="scale-90"
                    />
                    <Label className="text-xs">Required</Label>
                  </div>
                  <div>
                    <Label className="text-xs">Min</Label>
                    <Input
                      type="number"
                      min={0}
                      value={group.min_select}
                      onChange={(e) => updateGroup(gIdx, "min_select", Number(e.target.value))}
                      className="h-7 text-xs mt-0.5"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Max</Label>
                    <Input
                      type="number"
                      min={0}
                      value={group.max_select}
                      onChange={(e) => updateGroup(gIdx, "max_select", Number(e.target.value))}
                      className="h-7 text-xs mt-0.5"
                    />
                  </div>
                </div>
              </div>
              <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => removeGroup(gIdx)}>
                <Trash2 className="w-3 h-3 text-muted-foreground" />
              </Button>
            </div>

            {/* Answers */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Answers</span>
                <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={() => addAnswer(gIdx)}>
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-1">
                {group.answers.map((ans, aIdx) => (
                  <div key={aIdx} className="flex items-center gap-1">
                    <Input
                      value={ans.text}
                      onChange={(e) => updateAnswer(gIdx, aIdx, "text", e.target.value)}
                      placeholder="Option text"
                      className="h-7 text-xs flex-1"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={ans.price}
                      onChange={(e) => updateAnswer(gIdx, aIdx, "price", Number(e.target.value))}
                      className="h-7 text-xs w-20 text-right"
                      placeholder="$0.00"
                    />
                    <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => removeAnswer(gIdx, aIdx)}>
                      <X className="w-3 h-3 text-muted-foreground" />
                    </Button>
                  </div>
                ))}
                {group.answers.length === 0 && (
                  <p className="text-xs text-muted-foreground italic">No answers yet</p>
                )}
              </div>
            </div>

            {/* Linked items */}
            <div className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground">Link to items</span>
              <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                {items.map((item, iIdx) => {
                  const linked = group.linkedItems?.has(item.name) ?? false;
                  return (
                    <Badge
                      key={iIdx}
                      variant={linked ? "default" : "outline"}
                      className="cursor-pointer text-xs"
                      onClick={() => toggleItemLink(gIdx, item.name)}
                    >
                      {item.name}
                    </Badge>
                  );
                })}
                {items.length === 0 && <p className="text-xs text-muted-foreground italic">No items yet</p>}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 justify-between pt-2">
        <Button variant="outline" onClick={handleBack}>
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { onUpdate(groups); onSkip(); }}>
            Skip Modifiers
          </Button>
          <Button onClick={handleNext}>
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Step 4: Confirm & Import ─────────────────────────────────────────────────

function ConfirmStep({
  categories,
  items,
  modifierGroups,
  restaurantId,
  menuId,
  token,
  onBack,
  onDone,
}: {
  categories: ExtractedCategory[];
  items: ExtractedItem[];
  modifierGroups: ExtractedModifierGroup[];
  restaurantId: string;
  menuId: string;
  token: string;
  onBack: () => void;
  onDone: (result: { created: { categories: number; items: number; modifierGroups: number; links: number }; errors: string[] }) => void;
}) {
  const { toast } = useToast();
  const [mode, setMode] = useState<"create" | "merge">("create");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);

  const validCats = categories.filter(c => c.name.trim());
  const validItems = items.filter(i => i.name.trim());
  const validGroups = modifierGroups.filter(g => g.name.trim());
  const validCatNames = new Set(validCats.map(c => c.name.toLowerCase()));
  const itemsWithNoCategory = validItems.filter(i => !i.category || !validCatNames.has(i.category.toLowerCase()));
  const links: { itemName: string; questionName: string }[] = [];
  for (const g of validGroups) {
    const linkedArr = g.linkedItems ? Array.from(g.linkedItems) : [];
    for (const itemName of linkedArr) {
      links.push({ itemName, questionName: g.name });
    }
  }

  async function handleImport() {
    setLoading(true);
    setProgress("Preparing…");
    try {
      const jwt = localStorage.getItem("auth_token") || localStorage.getItem("token");
      setProgress("Creating categories and items…");

      const payload = {
        restaurantId,
        menuId,
        categories: validCats,
        items: validItems,
        modifierGroups: validGroups.map(g => ({
          name: g.name,
          format: g.format,
          required: g.required,
          min_select: g.min_select,
          max_select: g.max_select,
          answers: g.answers,
          suggested_items: Array.from(g.linkedItems ?? []),
        })),
        links: links.map(l => ({ itemName: l.itemName, questionName: l.questionName })),
        mode,
      };

      const res = await fetch("/api/admin/menu-import/commit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-eatzy-token": token,
          ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Import failed");
      setProgress(null);
      onDone(data);
    } catch (e: any) {
      setProgress(null);
      toast({ title: "Import failed", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-5 py-2">
      <h3 className="font-semibold text-sm">Ready to import</h3>

      <div className="rounded-md border bg-muted/30 p-4 space-y-1.5 text-sm">
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4 text-primary" />
          <span>{validCats.length} categories</span>
        </div>
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4 text-primary" />
          <span>{validItems.length} items</span>
        </div>
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4 text-primary" />
          <span>{validGroups.length} modifier groups</span>
        </div>
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4 text-primary" />
          <span>{links.length} modifier-to-item links</span>
        </div>
        {itemsWithNoCategory.length > 0 && (
          <div className="flex items-start gap-2 pt-1 text-amber-600 dark:text-amber-400">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span className="text-xs">
              {itemsWithNoCategory.length} item{itemsWithNoCategory.length !== 1 ? "s" : ""} have no valid category and will be created without one.
              Go back to assign categories before importing if needed.
            </span>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium">Import mode</Label>
        <RadioGroup value={mode} onValueChange={(v) => setMode(v as "create" | "merge")} className="space-y-2">
          <div className="flex items-start gap-2">
            <RadioGroupItem value="create" id="mode-create" className="mt-0.5" />
            <div>
              <Label htmlFor="mode-create" className="font-normal cursor-pointer">Create new (fresh menu)</Label>
              <p className="text-xs text-muted-foreground">Creates all categories, items, and modifier groups. Skips anything that already exists (matched by name).</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <RadioGroupItem value="merge" id="mode-merge" className="mt-0.5" />
            <div>
              <Label htmlFor="mode-merge" className="font-normal cursor-pointer">Merge with existing</Label>
              <p className="text-xs text-muted-foreground">Only creates items and categories that don't exist yet. Does not delete existing content.</p>
            </div>
          </div>
        </RadioGroup>
      </div>

      {progress && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          {progress}
        </div>
      )}

      <div className="flex gap-2 justify-between">
        <Button variant="outline" onClick={onBack} disabled={loading}>
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button onClick={handleImport} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PackageOpen className="w-4 h-4 mr-2" />}
          {loading ? "Importing…" : "Import"}
        </Button>
      </div>
    </div>
  );
}

// ─── Step 5: Done ─────────────────────────────────────────────────────────────

function DoneStep({
  result,
  onClose,
}: {
  result: { created: { categories: number; items: number; modifierGroups: number; links: number }; errors: string[] };
  onClose: () => void;
}) {
  const { created, errors } = result;
  return (
    <div className="space-y-5 py-2 text-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
          <Check className="w-6 h-6 text-primary" />
        </div>
        <h3 className="font-semibold text-base">Menu imported successfully</h3>
      </div>

      <div className="rounded-md border bg-muted/30 p-4 space-y-1.5 text-sm text-left max-w-xs mx-auto">
        <div className="flex justify-between"><span className="text-muted-foreground">Categories created</span><span className="font-medium">{created.categories}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">Items created</span><span className="font-medium">{created.items}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">Modifier groups</span><span className="font-medium">{created.modifierGroups}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">Modifier links</span><span className="font-medium">{created.links}</span></div>
      </div>

      {errors.length > 0 && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-left">
          <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-1.5">{errors.length} warning{errors.length > 1 ? "s" : ""}</p>
          <ul className="space-y-0.5">
            {errors.slice(0, 10).map((e, i) => (
              <li key={i} className="text-xs text-muted-foreground">{e}</li>
            ))}
            {errors.length > 10 && <li className="text-xs text-muted-foreground">…and {errors.length - 10} more</li>}
          </ul>
        </div>
      )}

      <Button onClick={onClose} className="w-full max-w-xs mx-auto">
        Go to Menu Editor
      </Button>
    </div>
  );
}

// ─── Main Wizard ──────────────────────────────────────────────────────────────

export default function MenuImportWizard({ open, onClose, restaurantId, menuId, token }: MenuImportWizardProps) {
  const [step, setStep] = useState(0);
  const [categories, setCategories] = useState<ExtractedCategory[]>([]);
  const [items, setItems] = useState<ExtractedItem[]>([]);
  const [modifierGroups, setModifierGroups] = useState<ExtractedModifierGroup[]>([]);
  const [importResult, setImportResult] = useState<{ created: { categories: number; items: number; modifierGroups: number; links: number }; errors: string[] } | null>(null);

  function handleClose() {
    setStep(0);
    setCategories([]);
    setItems([]);
    setModifierGroups([]);
    setImportResult(null);
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle>Import Menu</DialogTitle>
          <DialogDescription>
            Upload a menu image or PDF and AI will extract categories, items, and modifiers.
          </DialogDescription>
          <div className="pt-2">
            <StepIndicator current={step} />
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 px-1">
          {step === 0 && (
            <UploadStep
              token={token}
              onExtracted={(data) => {
                setCategories(data.categories ?? []);
                setItems(data.items ?? []);
                setModifierGroups((data.modifier_groups ?? []).map(g => ({ ...g, linkedItems: new Set<string>() })));
                setStep(1);
              }}
            />
          )}
          {step === 1 && (
            <CategoriesItemsStep
              categories={categories}
              items={items}
              onUpdate={(cats, itms) => { setCategories(cats); setItems(itms); }}
              onBack={() => setStep(0)}
              onNext={() => setStep(2)}
            />
          )}
          {step === 2 && (
            <ModifierStep
              modifierGroups={modifierGroups}
              items={items}
              onUpdate={(groups) => setModifierGroups(groups)}
              onBack={() => setStep(1)}
              onNext={() => setStep(3)}
              onSkip={() => setStep(3)}
            />
          )}
          {step === 3 && (
            <ConfirmStep
              categories={categories}
              items={items}
              modifierGroups={modifierGroups}
              restaurantId={restaurantId}
              menuId={menuId}
              token={token}
              onBack={() => setStep(2)}
              onDone={(result) => { setImportResult(result); setStep(4); }}
            />
          )}
          {step === 4 && importResult && (
            <DoneStep result={importResult} onClose={handleClose} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
